import { observer } from 'mobx-react-lite';
import { useResolve } from '@/infrastructure/useResolve';
import { ToastView } from './ToastView';
import { ThemeToggleView } from './ThemeToggleView';
import { TOKEN_AuthStore } from './AuthStore';
import { TOKEN_NavStore, type NavStore, type Screen } from './NavStore';
import { UsersView } from './admin/UsersView';
import { ClientsView } from './admin/ClientsView';
import { EventsView } from './admin/EventsView';
import { AccountView } from './account/AccountView';

const Tab = observer(function Tab({ nav, screen, label }: { nav: NavStore; screen: Screen; label: string }) {
  const active = nav.screen === screen;
  return (
    <button
      type="button"
      onClick={() => nav.go(screen)}
      data-testid={`tab-${screen}`}
      className={`-mb-px border-b-2 px-4 py-2 text-sm ${
        active ? 'border-indigo-600 font-medium text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
});

export const AppView = observer(function AppView() {
  const auth = useResolve(TOKEN_AuthStore);
  const nav = useResolve(TOKEN_NavStore);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="border-b border-slate-200 bg-gradient-to-r from-indigo-600 to-violet-600 text-white dark:border-slate-800">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/15 text-lg shadow-inner">🔐</span>
            <div>
              <h1 className="text-base font-semibold tracking-tight">Celeriant Auth · Admin</h1>
              <p className="text-xs text-white/70">user · client · session · audit management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {auth.isAuthenticated && (
              <span className="text-xs text-white/80" data-testid="signed-in-as">
                {auth.username ?? auth.sub}
              </span>
            )}
            <ThemeToggleView />
            {auth.isAuthenticated && (
              <button type="button" onClick={() => void auth.logout()} data-testid="logout" className="rounded bg-white/15 px-2 py-1 text-xs hover:bg-white/25">
                sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        {auth.status === 'loading' && <p className="text-sm text-slate-500">loading…</p>}

        {auth.status === 'error' && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-950/30" data-testid="auth-error">
            Sign-in failed: {auth.error}
            <button type="button" onClick={() => void auth.login()} className="ml-2 underline">
              try again
            </button>
          </div>
        )}

        {auth.status === 'anonymous' && (
          <div className="mx-auto max-w-md rounded-lg border border-slate-200 p-8 text-center dark:border-slate-800" data-testid="login-screen">
            <h2 className="mb-2 text-lg font-semibold">Sign in</h2>
            <p className="mb-4 text-sm text-slate-500">Manage users, clients, sessions, and the audit log.</p>
            <button type="button" onClick={() => void auth.login()} data-testid="login" className="rounded bg-indigo-600 px-5 py-2 text-sm font-medium text-white">
              Sign in with Celeriant Auth
            </button>
          </div>
        )}

        {auth.isAuthenticated && (
          <>
            <nav className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800" data-testid="nav">
              {auth.isAdmin && (
                <>
                  <Tab nav={nav} screen="users" label="Users" />
                  <Tab nav={nav} screen="clients" label="Clients" />
                  <Tab nav={nav} screen="events" label="Audit" />
                </>
              )}
              <Tab nav={nav} screen="account" label="My account" />
            </nav>

            {!auth.isAdmin && nav.screen !== 'account' && (
              <p className="text-sm text-slate-500" data-testid="not-admin">
                You are signed in but do not have the <code>admin</code> role. Only your own account is available.
              </p>
            )}

            {auth.isAdmin && nav.screen === 'users' && <UsersView />}
            {auth.isAdmin && nav.screen === 'clients' && <ClientsView />}
            {auth.isAdmin && nav.screen === 'events' && <EventsView />}
            {nav.screen === 'account' && <AccountView />}
          </>
        )}
      </main>

      <ToastView />
    </div>
  );
});
